import { COOKIE_REGISTRY, type CookieInfo, FIRST_PARTY_COOKIES } from "./SharedConstants";

/**
 * Version of the cookie inventory people are asked to consent to. Bump it whenever
 * FIRST_PARTY_COOKIES or THIRD_PARTY_COOKIES gains, loses or re-categorises a non-essential
 * cookie: a stored choice for an older version is ignored, so the banner asks again.
 */
export const COOKIE_CONSENT_VERSION = 8;

export const COOKIE_CONSENT_COOKIE = FIRST_PARTY_COOKIES.ACCEPTED_COOKIES_VERSION;

export interface CookieConsent {
  version: number;
  /** Whether functional (preference) cookies may be written. Essential cookies are always on. */
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export type CookieChoices = { functional: boolean; analytics?: boolean; marketing?: boolean };

/** All optional categories are explicit; pre-launch choices use the current inventory version. */
export const serializeCookieConsent = (consent: CookieChoices): string =>
  `${COOKIE_CONSENT_VERSION}.${Number(consent.functional)}.${Number(consent.analytics === true)}.${Number(consent.marketing === true)}`;

export const parseCookieConsent = (value: string | null | undefined): CookieConsent | null => {
  if (!value) return null;
  const match = /^(\d+)\.([01])\.([01])\.([01])$/.exec(value.trim());
  if (!match || Number(match[1]) !== COOKIE_CONSENT_VERSION) return null;
  return {
    version: Number(match[1]),
    functional: match[2] === "1",
    analytics: match[3] === "1",
    marketing: match[4] === "1",
  };
};

export const hasFunctionalCookieConsent = (value: string | null | undefined): boolean =>
  parseCookieConsent(value)?.functional === true;

/** Looks a cookie up in the inventory, including per-item cookies such as `CL_assetView_<id>`. */
export const getCookieInfo = (name: string): CookieInfo | undefined =>
  COOKIE_REGISTRY.find(
    (cookie) => cookie.name === name || (cookie.isPrefix && name.startsWith(`${cookie.name}_`)),
  );

/** True for any inventoried cookie that is not essential, so needs consent before it is written. */
export const requiresCookieConsent = (name: string): boolean => {
  const type = getCookieInfo(name)?.type;
  return type !== undefined && type !== "essential";
};

/** Consent is per category; allowing preferences never enables analytics. */
export const isCookieAllowed = (name: string, consent: CookieConsent | null): boolean => {
  const type = getCookieInfo(name)?.type;
  if (!type || type === "essential") return true;
  if (type === "marketing") return consent?.analytics === true && consent.marketing === true;
  return (type === "functional" || type === "analytics") && consent?.[type] === true;
};
